import { formatDate } from "Helpers/format-date";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { isEmpty, isArray, last } from "lodash";
import moment from "moment";

export const parcelModel = (parentData, index) => {
  const {
    data,
    loadAtData,
    unloadAtData,
    lastKnownLocationData,
    trainPlanData,
    trolleyData,
    boxData,
    returnInfo,
    ...otherData
  } = parentData || {};

  return {
    "#": index,
    "Order Code": data?.orderCode,
    "Created At": moment(otherData?.createdAt).format("DD-MM-YYYY hh:mm"),
    "Updated At": moment(otherData?.updatedAt).format("DD-MM-YYYY hh:mm"),
    "Delivery Date": data?.deliveryDate
      ? moment(data?.deliveryDate).format("DD-MM-YYYY hh:mm")
      : null,
    "Tracking Number": data?.trackingNumber,
    "Journey Code": trainPlanData?.journeyCode,
    "Trolley ID": trolleyData?.code,
    "Box ID": boxData?.code,
    "No of Parcels": data?.noOfParcel,
    "Total Volume (cm3)": data?.totalVolume,
    Weight: data?.weight,
    "Load Time": data?.loadDateTime
      ? moment(data?.loadDateTime).format("DD-MM-YYYY hh:mm")
      : null,
    "Load At": loadAtData?.locationName,
    "Unload Time": data?.unloadDateTime
      ? moment(data?.unloadDateTime).format("DD-MM-YYYY hh:mm")
      : null,
    "Unload At": unloadAtData?.locationName,
    "Last Known Location": lastKnownLocationData?.locationName,
    Status: toCapitalisedSpaced(data?.status),
    "Estimated CO2": data?.estimatedCO2,
    "Estimated CO2 Reduction": data?.estimatedCO2Reduction,
    "Estimated Road Ristance": data?.estimatedRoadDistance,
    "Estimated Road Reduction Distance": data?.estimatedRoadReductionDistance,
    "Delivery Attempts": returnInfo?.deliveryAttempts,
  };
};

export const boxModel = (parentData, index) => {
  const {
    data,
    loadAtData,
    unloadAtData,
    lastKnownLocationData,
    trolleyData,
    boxData,
    boxParcelData,
    ...otherData
  } = parentData || {};

  return {
    "#": index,
    "Box ID": boxData?.code,
    "Box Type": boxData?.boxType,
    "Trolley ID": trolleyData?.code,
    "Created At": moment(otherData?.createdAt).format("DD-MM-YYYY hh:mm"),
    "Updated At": moment(otherData?.updatedAt).format("DD-MM-YYYY hh:mm"),
    "Load Time": otherData?.loadDateTime
      ? moment(otherData?.loadDateTime).format("DD-MM-YYYY hh:mm")
      : null,
    "Load At": loadAtData?.locationName,
    "Unload Time": otherData?.unloadDateTime
      ? moment(otherData?.unloadDateTime).format("DD-MM-YYYY hh:mm")
      : null,
    "Unload At": unloadAtData?.locationName,
    "Last Known Location": lastKnownLocationData?.locationName,
    "No of Parcels": otherData?.noOfParcel,
    "Total Volume (cm3)": otherData?.totalParcelVolume,
    "Total Weight (kg)": otherData?.totalParcelWeight,
    "Weight Load Factor (%)": otherData?.weightLoadFactor,
    "Volume Load Factor (%)": otherData?.volumeLoadFactor,
    Status: toCapitalisedSpaced(otherData?.status),
  };
};

export const trolleyModel = (parentData, index) => {
  const {
    loadAtData,
    unloadAtData,
    lastKnownLocationData,
    trolleyData,
    boxData,
    ...otherData
  } = parentData || {};

  return {
    "#": index,
    "Trolley ID": trolleyData?.code,
    "Trolley Type": trolleyData?.trolleyType,
    "Created At": moment(otherData?.createdAt).format("DD-MM-YYYY hh:mm"),
    "Updated At": moment(otherData?.updatedAt).format("DD-MM-YYYY hh:mm"),
    "Load Time": otherData?.loadDateTime
      ? moment(otherData?.loadDateTime).format("DD-MM-YYYY hh:mm")
      : null,
    "Load At": loadAtData?.locationName,
    "Unload Time": otherData?.unloadDateTime
      ? moment(otherData?.unloadDateTime).format("DD-MM-YYYY hh:mm")
      : null,
    "Unload At": unloadAtData?.locationName,
    "Last Known Location": lastKnownLocationData?.locationName,
    "No of Parcels": otherData?.noOfParcel,
    "No of Boxes": otherData?.noOfBoxes,
    "No of Loose Parcels": otherData?.noOfLooseParcels,
    "No of Bulk Parcels": otherData?.noOfBulkParcels,
    "Total Volume (cm3)": otherData?.totalVolume,
    "Total Weight (kg)": otherData?.totalWeight,
    "Weight Load Factor (%)": otherData?.weightLoadFactor,
    "Volume Load Factor (%)": otherData?.volumeLoadFactor,
    Status: toCapitalisedSpaced(otherData?.status),
  };
};

// ====================================================
//   Train Excel Model
// ====================================================

export const handleProcessTrainResponse = (parentData) => {
  if (isEmpty(parentData)) return [];

  let expandedData: any = [];

  // for each parentData, spread out to make each trainStops inside another row
  parentData.forEach((data, index) => {
    // push the first
    expandedData = [
      ...expandedData,
      { ...data, dataType: "PARENT", row: index + 1 },
    ];

    // push the rest of the children
    const { trainStops } = data || {};

    const trainStopsData = trainStops.map((t) => ({ ...t, dataType: "CHILD" }));
    expandedData = [...expandedData, ...trainStopsData];
  });

  return expandedData;
};

const findTrolleyData = (trolleyParcelData, trolleyID) => {
  if (!isArray(trolleyParcelData)) return null;
  if (!trolleyID) return null;

  return trolleyParcelData.find((parcelObj) => parcelObj._id == trolleyID);
};

const constructTrolleys = (data, referenceData) => {
  if (!isArray(data)) return null;

  return data.reduce((acc, curr, index) => {
    const trolley = findTrolleyData(referenceData, curr?.id);
    const trolleyCode = trolley?.trolleyCode;

    if (trolleyCode) {
      if (index === data.length - 1) return acc + trolleyCode;
      return acc + trolleyCode + ", ";
    }

    return acc;
  }, "");
};

const constructTrolleyDates = (data, str: string = "loadAt") => {
  if (!isArray(data)) return null;

  return data.reduce((acc, curr, index) => {
    const date = formatDate(curr?.[str], "DD-MM-YYYY hh:mm a");

    if (date) {
      if (index === data.length - 1) return acc + date;
      return acc + date + ", ";
    }

    return acc;
  }, "");
};

export const trainModel = (parentData, index) => {
  // there are two types of data
  // -> 1. Train line data (show number)
  // -> 2. Train line stops data (do not show number)

  // ensure the base columns are the same for both

  if (parentData.dataType === "PARENT") {
    const { lastKnownLocationData, trainLineData, ...otherData } =
      parentData || {};

    const startStation = trainLineData?.stations?.find(
      (station) => otherData?.startStation == station?.stationCode,
    );

    const lastStation = trainLineData?.stations?.find(
      (station) => otherData?.lastStation == station?.stationCode,
    );

    return {
      "#": parentData?.row,
      "Train Line": trainLineData?.name,
      "Journey Code": otherData?.journeyCode,
      "Created At": moment(otherData?.createdAt).format("DD-MM-YYYY hh:mm"),
      "Updated At": moment(otherData?.updatedAt).format("DD-MM-YYYY hh:mm"),
      "Train Plate": otherData?.plateNo,
      "Start Station": startStation?.stationName,
      "Last Station": lastStation?.stationName,
      "Planned Departure": otherData?.plannedDeparture
        ? moment(otherData?.plannedDeparture).format("DD-MM-YYYY hh:mm")
        : null,
      "Actual Departure": otherData?.actualDeparture
        ? moment(otherData?.actualDeparture).format("DD-MM-YYYY hh:mm")
        : null,
      "Status (Train)": toCapitalisedSpaced(otherData?.status),
      "Station Code": null,
      "Estimated Arrival": null,
      "Trolley In": null,
      "Trolley In Time": null,
      "Trolley Out": null,
      "Trolley Out Time": null,
      "Status (Station)": null,
    };
  }

  const {
    plannedDeparture,
    stationCode,
    status,
    estimatedArrival,
    loadTrolleys,
    unloadTrolleys,
    trolleyParcelData,
  } = parentData || {};

  // find the trolley and time
  let loadTrolleyStr: string | null = null;
  let loadTrolleyDateStr: string | null = null;
  let unloadTrolleyStr: string | null = null;
  let unloadTrolleyDateStr: string | null = null;

  if (loadTrolleys) {
    loadTrolleyStr = constructTrolleys(
      loadTrolleys?.trolleyParcels,
      trolleyParcelData,
    );
    loadTrolleyDateStr = constructTrolleyDates(loadTrolleys?.trolleyParcels);
  }

  if (unloadTrolleys) {
    unloadTrolleyStr = constructTrolleys(
      unloadTrolleys?.trolleyParcels,
      trolleyParcelData,
    );
    loadTrolleyDateStr = constructTrolleyDates(unloadTrolleys?.trolleyParcels);
  }

  return {
    "#": null,
    "Train Line": null,
    "Journey Code": null,
    "Created At": null,
    "Updated At": null,
    "Train Plate": null,
    "Start Station": null,
    "Last Station": null,
    "Actual Departure": null,
    "Status (Train)": null,
    "Station Code": stationCode,
    "Planned Departure": formatDate(plannedDeparture, "DD-MM-YYYY hh:mm"),
    "Estimated Arrival": formatDate(estimatedArrival, "DD-MM-YYYY hh:mm"),
    "Trolley In": loadTrolleyStr,
    "Trolley In Time": loadTrolleyDateStr,
    "Trolley Out": unloadTrolleyStr,
    "Trolley Out Time": unloadTrolleyDateStr,
    "Status (Station)": toCapitalisedSpaced(status),
  };
};
